import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// --- МОДЕЛИ ДАННЫХ ДЛЯ ФОРМ ---
data class QuestionInput(
    var textContent: String = "", // Для Tarea 1 и Tarea 5 (если нужно)
    var question: String = "",
    var optionA: String = "",
    var optionB: String = "",
    var optionC: String = "",
    var optionD: String = "", // Для Tarea 4
    var optionE: String = "", // Для Tarea 4
    var correctOption: String = "A"
)

@Composable
fun AdminGeneratorScreen(onBack: () -> Unit) {
    var selectedTab by remember { mutableStateOf(1) }
    var generatedSql by remember { mutableStateOf("") }
    var setTitle by remember { mutableStateOf("") }
    var isExam by remember { mutableStateOf(false) } // Маркер экзамена

    // Общие стили
    val containerStyle: StyleScope.() -> Unit = {
        padding(20.px); maxWidth(1000.px); property("margin", "0 auto")
        fontFamily("Roboto, sans-serif"); paddingBottom(100.px)
    }

    // Функция генерации (делегирует конкретным генераторам)
    fun generate() {
        val finalTitle = if (isExam) "EXAMEN - $setTitle" else setTitle
        generatedSql = when (selectedTab) {
            1 -> generateSqlT1(finalTitle)
            2 -> generateSqlT2(finalTitle)
            3 -> generateSqlT3(finalTitle)
            4 -> generateSqlT4(finalTitle)
            5 -> generateSqlT5(finalTitle)
            else -> ""
        }
    }

    Div({ style(containerStyle) }) {
        // --- ШАПКА ---
        Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
            H2 { Text("🛠️ Генератор задач (SQL)") }
            Button({ onClick { onBack() }; style { padding(8.px, 15.px); cursor("pointer") } }) { Text("Выход") }
        }

        // --- ВЫБОР РЕЖИМА (ВКЛАДКИ) ---
        Div({ style { display(DisplayStyle.Flex); gap(10.px); marginBottom(20.px); flexWrap(FlexWrap.Wrap) } }) {
            (1..5).forEach { i ->
                Button({
                    onClick { selectedTab = i; generatedSql = "" }
                    style {
                        padding(10.px, 20.px); cursor("pointer"); border(0.px); borderRadius(5.px)
                        backgroundColor(if (selectedTab == i) Color("#3498db") else Color("#ecf0f1"))
                        color(if (selectedTab == i) Color.white else Color("#2c3e50"))
                        fontWeight("bold")
                    }
                }) { Text("Tarea $i") }
            }
        }

        // --- ОБЩИЕ ПОЛЯ ---
        Div({ style { backgroundColor(Color.white); padding(20.px); borderRadius(10.px); marginBottom(20.px); property("box-shadow", "0 2px 5px rgba(0,0,0,0.1)") } }) {
            H4 { Text("Настройки Сета") }
            Input(InputType.Text) {
                placeholder("Название темы (например: El Medioambiente)")
                value(setTitle)
                onInput { setTitle = it.value }
                style { width(100.percent); padding(10.px); marginBottom(10.px); fontSize(1.1.cssRem) }
            }
            Label {
                Input(InputType.Checkbox) {
                    checked(isExam)
                    onChange { isExam = it.value }
                }
                Text(" Пометить как Экзамен (добавит 'EXAMEN' в название)")
            }
        }

        // --- КОНТЕНТ ФОРМЫ ---
        when (selectedTab) {
            1 -> FormT1()
            2 -> FormT2()
            3 -> FormT3()
            4 -> FormT4()
            5 -> FormT5()
        }

        // --- КНОПКА ГЕНЕРАЦИИ ---
        Div({ style { textAlign("center"); marginTop(30.px); marginBottom(20.px) } }) {
            Button({
                onClick { generate() }
                style {
                    padding(15.px, 40.px); backgroundColor(Color("#27ae60")); color(Color.white)
                    border(0.px); borderRadius(8.px); fontSize(1.2.cssRem); cursor("pointer")
                }
            }) { Text("Сгенерировать SQL для Tarea $selectedTab") }
        }

        // --- ВЫВОД ---
        if (generatedSql.isNotEmpty()) {
            Div({ style { backgroundColor(Color("#2c3e50")); padding(15.px); borderRadius(8.px) } }) {
                H4({ style { color(Color.white) } }) { Text("SQL Результат:") }
                TextArea {
                    value(generatedSql)
                    attr("readonly", "true"); rows(15)
                    style { width(100.percent); fontFamily("monospace"); backgroundColor(Color("#34495e")); color(Color("#fff")); border(0.px); padding(10.px) }
                }
            }
        }
    }
}

// =====================================================================
// ФОРМЫ И ГЕНЕРАТОРЫ ДЛЯ КАЖДОЙ ЗАДАЧИ
// =====================================================================

// Глобальные переменные состояния для форм (чтобы не сбрасывались при переключении табов, можно вынести в ViewModel, но здесь сойдет)
// Для простоты используем объекты-синглтоны или rememberSaveable, но в Web Compose remember работает пока компонент жив.
// Чтобы данные сохранялись при переключении вкладок, объявим их внутри AdminGeneratorScreen или глобально.
// Для упрощения кода здесь, я буду использовать remember внутри самих форм, НО ПРИ ПЕРЕКЛЮЧЕНИИ ВКЛАДОК ДАННЫЕ БУДУТ СБРАСЫВАТЬСЯ.
// Если нужно сохранять - поднимите state вверх.

// --- TAREA 1 (5 коротких текстов) ---
var t1Questions = mutableStateListOf(*(Array(5) { QuestionInput() }))

@Composable
fun FormT1() {
    t1Questions.forEachIndexed { index, q ->
        QuestionCard("Вопрос ${index + 1}", q, showText = true, showOptions = true)
    }
}

fun generateSqlT1(title: String): String {
    val sb = StringBuilder()
    sb.appendHeader(title, 1)
    t1Questions.forEachIndexed { i, q ->
        sb.appendItem(q.textContent, q.question, q.optionA, q.optionB, q.optionC, null, null, q.correctOption, i + 1)
    }
    sb.appendFooter()
    return sb.toString()
}


// --- TAREA 2 (1 длинный текст + 5 вопросов) ---
var t2MainText by mutableStateOf("")
var t2Questions = mutableStateListOf(*(Array(5) { QuestionInput() }))

@Composable
fun FormT2() {
    BigTextCard("Текст статьи (Tarea 2)", t2MainText) { t2MainText = it }
    t2Questions.forEachIndexed { index, q ->
        QuestionCard("Вопрос ${index + 1}", q, showText = false, showOptions = true)
    }
}

fun generateSqlT2(title: String): String {
    val sb = StringBuilder()
    sb.appendHeader(title, 2)
    // В Tarea 2 текст обычно один на всех. В модели PracticeScreen он разбивался или дублировался.
    // Лучший способ: записать полный текст в первый вопрос, или дублировать.
    // Для совместимости с текущим reading.Task2Layout запишем текст в ПЕРВЫЙ вопрос, в остальные пустую строку.
    t2Questions.forEachIndexed { i, q ->
        val text = if (i == 0) t2MainText else "" // Текст храним в первом элементе
        sb.appendItem(text, q.question, q.optionA, q.optionB, q.optionC, null, null, q.correctOption, i + 1)
    }
    sb.appendFooter()
    return sb.toString()
}


// --- TAREA 3 (3 Текста + 8 Утверждений) ---
var t3TextA by mutableStateOf("")
var t3TextB by mutableStateOf("")
var t3TextC by mutableStateOf("")
var t3Questions = mutableStateListOf(*(Array(8) { QuestionInput() })) // 8 утверждений

@Composable
fun FormT3() {
    BigTextCard("Texto A", t3TextA) { t3TextA = it }
    BigTextCard("Texto B", t3TextB) { t3TextB = it }
    BigTextCard("Texto C", t3TextC) { t3TextC = it }

    H3 { Text("Утверждения (8 штук)") }
    t3Questions.forEachIndexed { index, q ->
        Div({ style { backgroundColor(Color.white); padding(15.px); marginBottom(10.px); borderRadius(8.px) } }) {
            H5 { Text("Enunciado ${index + 1}") }
            Input(InputType.Text) {
                value(q.question)
                placeholder("Например: Es adecuado para familias...")
                onInput { t3Questions[index] = t3Questions[index].copy(question = it.value) }
                style { width(100.percent); padding(8.px); marginBottom(5.px) }
            }
            Label { Text("Ответ: ") }
            Select({
                onInput { t3Questions[index] = t3Questions[index].copy(correctOption = it.value.toString()) }
            }) {
                Option("A") { Text("Texto A") }
                Option("B") { Text("Texto B") }
                Option("C") { Text("Texto C") }
            }
        }
    }
}

fun generateSqlT3(title: String): String {
    val sb = StringBuilder()
    sb.appendHeader(title, 3)
    // Собираем общий текст
    val fullText = "TEXTO A: $t3TextA\n\nTEXTO B: $t3TextB\n\nTEXTO C: $t3TextC"

    t3Questions.forEachIndexed { i, q ->
        val text = if (i == 0) fullText else ""
        // В Tarea 3 варианты ответов фиксированы (Texto A, B, C), в базе они могут быть названиями или просто A/B/C
        // Запишем названия текстов как опции для наглядности
        sb.appendItem(text, q.question, "Texto A", "Texto B", "Texto C", null, null, q.correctOption, i + 1)
    }
    sb.appendFooter()
    return sb.toString()
}


// --- TAREA 4 (Текст с пропусками + Пул фраз A-E + 5 Вопросов) ---
// Примечание: В SIELE обычно 8 вариантов (A-H), но наша модель ReadingQuestion ограничена полями A-E.
// Будем использовать 5 вариантов.
var t4MainText by mutableStateOf("")
var t4OptionA by mutableStateOf("")
var t4OptionB by mutableStateOf("")
var t4OptionC by mutableStateOf("")
var t4OptionD by mutableStateOf("")
var t4OptionE by mutableStateOf("")
var t4Answers = mutableStateListOf("A", "B", "C", "D", "E") // Ответы для 5 пропусков

@Composable
fun FormT4() {
    BigTextCard("Текст с пропусками (используйте (1), (2)...)", t4MainText) { t4MainText = it }

    Div({ style { backgroundColor(Color.white); padding(20.px); marginBottom(20.px); borderRadius(8.px) } }) {
        H4 { Text("Фразы для вставки (Pool)") }
        InputOption("Фраза A", t4OptionA) { t4OptionA = it }
        InputOption("Фраза B", t4OptionB) { t4OptionB = it }
        InputOption("Фраза C", t4OptionC) { t4OptionC = it }
        InputOption("Фраза D", t4OptionD) { t4OptionD = it }
        InputOption("Фраза E", t4OptionE) { t4OptionE = it }
    }

    H4 { Text("Правильные ответы для пропусков") }
    (0..4).forEach { i ->
        Div({ style { marginBottom(5.px) } }) {
            Span { Text("Espacio ${i + 1}: ") }
            Select({ onInput { t4Answers[i] = it.value.toString() } }) {
                listOf("A", "B", "C", "D", "E").forEach { opt -> Option(opt) { Text(opt) } }
            }
        }
    }
}

fun generateSqlT4(title: String): String {
    val sb = StringBuilder()
    sb.appendHeader(title, 4)
    // В Tarea 4 опции одинаковы для всех вопросов (это пул). Записываем их в каждый вопрос (или в первый).
    // Текущая реализация reading.Task4Layout берет опции из первого вопроса.
    (0..4).forEach { i ->
        val text = if (i == 0) t4MainText else ""
        val questionText = "Espacio ${i + 1}"
        sb.appendItem(
            text, questionText,
            t4OptionA, t4OptionB, t4OptionC, t4OptionD, t4OptionE,
            t4Answers[i], i + 1
        )
    }
    sb.appendFooter()
    return sb.toString()
}


// --- TAREA 5 (Текст с пропусками + 12 вопросов с вариантами) ---
var t5MainText by mutableStateOf("")
var t5Questions = mutableStateListOf(*(Array(12) { QuestionInput() }))

@Composable
fun FormT5() {
    BigTextCard("Текст с пропусками (1)...(12)", t5MainText) { t5MainText = it }

    // Сетка для 12 вопросов, чтобы не занимать много места
    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns", "1fr 1fr"); gap(10.px) } }) {
        t5Questions.forEachIndexed { index, q ->
            Div({ style { backgroundColor(Color.white); padding(10.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Color("#eee")) } }) {
                H5 { Text("Espacio ${index + 1}") }
                Input(InputType.Text) {
                    value(q.optionA); placeholder("Opción A")
                    onInput { t5Questions[index] = t5Questions[index].copy(optionA = it.value) }
                    style { width(100.percent); marginBottom(3.px) }
                }
                Input(InputType.Text) {
                    value(q.optionB); placeholder("Opción B")
                    onInput { t5Questions[index] = t5Questions[index].copy(optionB = it.value) }
                    style { width(100.percent); marginBottom(3.px) }
                }
                Input(InputType.Text) {
                    value(q.optionC); placeholder("Opción C")
                    onInput { t5Questions[index] = t5Questions[index].copy(optionC = it.value) }
                    style { width(100.percent); marginBottom(3.px) }
                }
                Div {
                    Label { Text("Correcto: ") }
                    Select({ onInput { t5Questions[index] = t5Questions[index].copy(correctOption = it.value.toString()) } }) {
                        Option("A") { Text("A") }
                        Option("B") { Text("B") }
                        Option("C") { Text("C") }
                    }
                }
            }
        }
    }
}

fun generateSqlT5(title: String): String {
    val sb = StringBuilder()
    sb.appendHeader(title, 5)
    t5Questions.forEachIndexed { i, q ->
        val text = if (i == 0) t5MainText else ""
        val questionText = "Espacio ${i + 1}"
        sb.appendItem(
            text, questionText,
            q.optionA, q.optionB, q.optionC, null, null,
            q.correctOption, i + 1
        )
    }
    sb.appendFooter()
    return sb.toString()
}


// =====================================================================
// UI КОМПОНЕНТЫ И УТИЛИТЫ
// =====================================================================

@Composable
fun BigTextCard(label: String, textValue: String, onUpdate: (String) -> Unit) {
    Div({ style { backgroundColor(Color.white); padding(15.px); marginBottom(15.px); borderRadius(8.px) } }) {
        H5 { Text(label) }
        TextArea {
            value(textValue)
            onInput { onUpdate(it.value) }
            rows(5)
            style { width(100.percent); padding(8.px); fontFamily("sans-serif") }
        }
    }
}

@Composable
fun QuestionCard(title: String, q: QuestionInput, showText: Boolean, showOptions: Boolean) {
    Div({ style { backgroundColor(Color.white); padding(15.px); marginBottom(15.px); borderRadius(8.px); border(1.px, LineStyle.Solid, Color("#eee")) } }) {
        H5({ style { color(Color("#3498db")); marginTop(0.px) } }) { Text(title) }

        if (showText) {
            Input(InputType.Text) {
                value(q.textContent); placeholder("Текст отрывка")
                onInput { q.textContent = it.value } // Обновление state напрямую (работает для mutableStateListOf)
                style { width(100.percent); marginBottom(8.px); padding(5.px) }
            }
        }

        Input(InputType.Text) {
            value(q.question); placeholder("Вопрос")
            onInput { q.question = it.value }
            style { width(100.percent); marginBottom(8.px); padding(5.px); fontWeight("bold") }
        }

        if (showOptions) {
            Div({ style { display(DisplayStyle.Flex); gap(5.px) } }) {
                Input(InputType.Text) { value(q.optionA); placeholder("A"); onInput { q.optionA = it.value }; style { flex(1); padding(5.px) } }
                Input(InputType.Text) { value(q.optionB); placeholder("B"); onInput { q.optionB = it.value }; style { flex(1); padding(5.px) } }
                Input(InputType.Text) { value(q.optionC); placeholder("C"); onInput { q.optionC = it.value }; style { flex(1); padding(5.px) } }
            }
        }

        Div({ style { marginTop(8.px) } }) {
            Label { Text("Правильный ответ: ") }
            Select({ onInput { q.correctOption = it.value.toString() } }) {
                Option("A") { Text("A") }
                Option("B") { Text("B") }
                Option("C") { Text("C") }
            }
        }
    }
}

@Composable
fun InputOption(label: String, value: String, onUpdate: (String) -> Unit) {
    Div({ style { marginBottom(5.px) } }) {
        Label { Text("$label: ") }
        Input(InputType.Text) {
            value(value)
            onInput { onUpdate(it.value) }
            style { width(70.percent); padding(5.px) }
        }
    }
}


// --- SQL HELPERS ---

fun String.sqlSafe(): String = this.replace("'", "''")

fun StringBuilder.appendHeader(title: String, type: Int) {
    append("DO $$\nDECLARE\n    sid uuid := gen_random_uuid();\nBEGIN\n")
    append("    INSERT INTO reading_sets (id, title, task_type) VALUES (sid, '${title.sqlSafe()}', $type);\n")
    append("    INSERT INTO reading_items (set_id, text_content, question, option_a, option_b, option_c, option_d, option_e, correct_option, order_index) VALUES\n")
}

fun StringBuilder.appendItem(
    text: String, quest: String, a: String, b: String, c: String, d: String?, e: String?, corr: String, idx: Int
) {
    val dVal = d?.let { "'${it.sqlSafe()}'" } ?: "NULL"
    val eVal = e?.let { "'${it.sqlSafe()}'" } ?: "NULL"
    val end = if (idx == if (text.startsWith("EXAMEN - Tarea 5")) 12 else if (text.startsWith("EXAMEN - Tarea 3")) 8 else 5) ";" else ","
    // Упростим логику запятых: всегда запятая, а потом вручную заменим последнюю на ; или просто закроем блок
    // Лучше просто всегда запятую и потом удалить последнюю
    append("    (sid, '${text.sqlSafe()}', '${quest.sqlSafe()}', '${a.sqlSafe()}', '${b.sqlSafe()}', '${c.sqlSafe()}', $dVal, $eVal, '$corr', $idx),\n")
}

fun StringBuilder.appendFooter() {
    // Удаляем последнюю запятую и перенос строки, ставим ;
    val len = this.length
    if (this[len - 2] == ',') {
        this.setLength(len - 2)
        append(";\nEND $$;")
    } else {
        append("END $$;")
    }
}